/**
 * Подбор релевантных курсов и пакетов.
 *
 * Раньше блок «Похожие курсы» показывал просто первые четыре курса из массива —
 * одни и те же на всех страницах. Здесь подбор идёт по смыслу: сначала пакеты,
 * в которые входит текущий курс (самый выгодный апсейл), затем курсы той же
 * категории.
 *
 * Связь курса с пакетом определяется по skillspaceCourses: у пакета в этом поле
 * перечислены courseId всех входящих курсов.
 */

const PACKAGE_CATEGORY = "Пакеты курсов";

const skillspaceIds = (course) =>
  (course?.skillspaceCourses || []).map((c) => c.courseId).filter(Boolean);

const isVisible = (course, current) =>
  course && !course.hidden && course.id !== current.id;

/** Пакеты, в которые входит этот курс. */
export function findPackagesWithCourse(course, allCourses) {
  const ids = skillspaceIds(course);
  if (!ids.length || course.category === PACKAGE_CATEGORY) return [];

  return allCourses.filter(
    (c) =>
      isVisible(c, course) &&
      c.category === PACKAGE_CATEGORY &&
      skillspaceIds(c).some((id) => ids.includes(id))
  );
}

/**
 * Сколько экономит пакет против покупки курсов по отдельности.
 * Возвращает null, если состав пакета определить не удалось.
 */
export function calcPackageSaving(pack, allCourses) {
  const ids = skillspaceIds(pack);
  if (ids.length < 2) return null;

  const included = ids
    .map((id) =>
      allCourses.find(
        (c) =>
          c.category !== PACKAGE_CATEGORY &&
          skillspaceIds(c).includes(id)
      )
    )
    .filter(Boolean);

  // Считаем выгоду, только если нашли все курсы пакета
  if (included.length !== ids.length) return null;

  const separately = included.reduce((sum, c) => sum + (c.price || 0), 0);
  const saving = separately - (pack.price || 0);
  return saving > 0 ? { saving, separately, included } : null;
}

/**
 * Релевантные курсы: пакеты с этим курсом → та же категория →
 * пересечение по дополнительным категориям → остальные.
 */
export function getRelatedCourses(course, allCourses, limit = 4) {
  if (!course) return [];

  const extra = course.additionalCategories || [];

  const scored = allCourses
    .filter((c) => isVisible(c, course))
    .map((c) => {
      let score = 0;

      if (c.category === course.category) score += 10;

      const overlap = (c.additionalCategories || []).filter((cat) =>
        extra.includes(cat)
      ).length;
      score += overlap * 3;

      if (c.level && c.level === course.level) score += 2;

      // Пакеты показываем, но не даём им вытеснить профильные курсы
      if (c.category === PACKAGE_CATEGORY) score += 1;

      return { course: c, score };
    })
    .sort((a, b) => b.score - a.score || (b.course.students || 0) - (a.course.students || 0));

  const packages = findPackagesWithCourse(course, allCourses);
  const packageIds = new Set(packages.map((p) => p.id));

  // Пакеты с этим курсом идут первыми — это прямой апсейл
  const ordered = [
    ...packages,
    ...scored.map((s) => s.course).filter((c) => !packageIds.has(c.id)),
  ];

  return ordered.slice(0, limit);
}
